import React, { useState } from 'react';
import { Input } from 'antd';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { ALL_EXCLUDED, SENT_TO_CRM_AO3, SOURCE } from '../../../core/constants';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import { IBook } from '../../../section/Common/types/types';
import Modal from '../../../components/Modal';
import { useActions } from '../../../context/Actions.context';
import { useServices } from '../../../core/services/useServices';
import showActionNotification from '../../../section/Common/utils/showActionNotification';
import Endpoints from '../../../core/constants/endpoints';
import Ao3ExcludeOptions from './Ao3ExcludeOptions';

interface IProps {
  book: IBook | any;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const Ao3ExcludeTitles: React.FC<IProps> = ({ book, modal, toggleModal, id }) => {
  const { setActionLoading } = useActions();
  const { book: bookService } = useServices();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { mutateAsync: mutateExclude } = useUpdateTitle(ALL_EXCLUDED);

  const [selectionMode, setSelectionMode] = useState<'title' | 'series'>('title');
  const [notes, setNotes] = useState<string>('');

  const handleSelectionChange = (e) => {
    setSelectionMode(e?.target?.value);
  };

  const handleReset = () => {
    setSelectionMode('title');
    setNotes('');
  };

  const onSend = () => {
    setActionLoading(true);

    const body = {
      ...(notes.trim() && { notes: notes.trim() })
    };

    const isSeries = selectionMode === 'series';
    const params = {
      work_id: book.work_id,
      title: book.Title,
      ...(isSeries && { seriesid: book?.seriesid }),
      tag: isSeries ? 'Exclude Entire Series' : ''
    };

    const promise = mutateExclude({ params, body });

    promise.then(res => {
      if (res?.status === 200) {
        showActionNotification({
          type: 'success',
          message: 'The Title was successfully excluded.',
          destination: {
            name: 'All Excluded',
            onGoTo: () => history.push(`${SOURCE.ao3}/all-excluded`)
          },
          onUndo: async () => {
            await bookService.updateTitle(
              {
                source: SOURCE.ao3,
                sourceTab: 'all-excluded',
                endpoint: Endpoints[id],
                action: id
              },
              { work_id: book.work_id }
            );
          },
          queryClient,
          id
        });
      } else {
        showActionNotification({
          type: 'error',
          message: 'The Title has not been sent.'
        });
      }
    }).catch(() => {
      showActionNotification({
        type: 'error',
        message: 'The Title has not been sent.'
      });
    });

    promise.then(() => {
      if (id === SENT_TO_CRM_AO3) {
        showActionNotification({ type: 'info', message: 'Please delete the data from Hubspot.' });
      }
    });

    promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
    promise.then(() => setActionLoading(false));
    promise.then(() => queryClient.refetchQueries(`get-${id}`));

    toggleModal();
    handleReset();
  };

  return (
    <Modal
      okText='Exclude Title'
      title='Send Title to Exclusion List'
      visible={modal}
      onOk={onSend}
      onCancel={() => {
        toggleModal();
        handleReset();
      }}
    >
      <Ao3ExcludeOptions
        selectionMode={selectionMode}
        onSelectionChange={handleSelectionChange}
        hasSeries={Boolean(book?.Series)}
      />

      <Input.TextArea
        placeholder='Leave a note stating the reason (optional)'
        maxLength={400}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
      />

      <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
        {notes.length} / 400
      </div>
    </Modal>
  );
};

export default React.memo(Ao3ExcludeTitles);
